import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getCount } from '../api/get-count';
import { QUERY_KEYS } from '../constants';
import type { ListArguments } from '../types';

interface UseGetCountOptions<Instance extends BaseInstance> {
  modelName: ModelName;
  filters?: ListArguments<Instance>['queryParams']['filters'];
  enableGlobalFilters?: boolean;
  overrideUrl?: string;
  overrideUrlReplaceAll?: boolean;
}

export const useGetCount = <Instance extends BaseInstance = BaseInstance>(
  options: UseGetCountOptions<Instance> &
    Omit<UseQueryOptions<number>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [
      options.modelName,
      QUERY_KEYS.COUNT,
      {
        ...options.filters,
        ...(options.overrideUrl && { overrideUrl: options.overrideUrl }),
        ...(options.overrideUrlReplaceAll && {
          overrideUrlReplaceAll: options.overrideUrlReplaceAll,
        }),
        ...(options.enableGlobalFilters && {
          enableGlobalFilters: options.enableGlobalFilters,
        }),
      },
    ],
    queryFn: async ({ signal }) => {
      return await getCount({
        modelName: options.modelName,
        filters: options.filters,
        enableGlobalFilters: options.enableGlobalFilters,
        overrideUrl: options.overrideUrl,
        overrideUrlReplaceAll: options.overrideUrlReplaceAll,
        signal,
      });
    },
    refetchOnWindowFocus: false,
    retry: 1,
    ...options,
  });
};
